import { GripVertical } from 'lucide-react';
import { formatINR } from '@/utils/formatters';
import type { WidgetDefinition } from './widgetRegistry';
import type { WidgetDataMap } from '@/hooks/useWidgetData';

interface Props {
  widget: WidgetDefinition;
  data: WidgetDataMap;
  loading: boolean;
}

// List of widgets where null truly means "data source not wired up yet" (manual
// entry / IoT required). Everything else should show 0 when null — Kunwar Bug 14.
const PLACEHOLDER_WIDGETS = new Set([
  'owner.oee',
  'owner.order_to_delivery',
  'owner.delivery_sla',
  'owner.product_mix_margin',
  'production.daily_output',
  'production.target_achievement',
  'production.defect_rate',
  'production.machine_utilization',
  'service.csat_score',
]);

function formatValue(value: number | string | null | undefined, format: string, widgetId?: string): string {
  // Placeholders stay as em-dash — metric not yet computable
  if (value == null && widgetId && PLACEHOLDER_WIDGETS.has(widgetId)) return '\u2014';
  // For every other widget, null means "no records yet" → 0
  if (value == null) value = 0;

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (typeof num !== 'number' || !isFinite(num)) return String(value);

  // Guard: catch absurdly large numbers that would render in scientific
  // notation (Ishan F-11 — deal values showing 4.5e+107). Anything above
  // 1e15 is almost certainly a data corruption bug; clamp + warn so the
  // demo doesn't render "e+107" to the customer.
  const safeNum = Math.abs(num) > 1e15 ? 0 : num;

  switch (format) {
    case 'currency': return formatINR(safeNum, false);
    case 'percentage': return `${safeNum}%`;
    case 'days': return `${safeNum}d`;
    case 'number': return safeNum.toLocaleString('en-IN');
    default: return safeNum.toLocaleString('en-IN');
  }
}

export function WidgetCard({ widget, data, loading }: Props) {
  const Icon = widget.icon;
  const raw = data[widget.id];
  const display = loading ? '' : formatValue(raw, widget.format, widget.id);

  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      {/* Drag handle header */}
      <div className="widget-drag-handle flex cursor-grab items-center justify-between border-b border-gray-100 px-3 py-2 active:cursor-grabbing">
        <div className="flex items-center gap-2">
          <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${widget.color}`}>
            <Icon size={14} className={widget.iconColor} />
          </div>
          <span className="text-[11px] font-medium text-gray-500 leading-tight line-clamp-1">{widget.label}</span>
        </div>
        <GripVertical size={14} className="text-gray-300" />
      </div>

      {/* Value */}
      <div className="flex flex-1 items-center px-3 py-2">
        {loading ? (
          <div className="h-6 w-24 animate-pulse rounded bg-gray-100" />
        ) : (
          <p className={`text-xl font-bold ${raw == null ? 'text-gray-300' : 'text-gray-900'}`}>
            {display}
          </p>
        )}
      </div>
    </div>
  );
}
